import React from 'react';
import { useIntl } from 'react-intl';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../../common/views/SMLButton/SMLButton';
import { useSmartLinkAddress } from '../../../../../music24/promote/releases/useSmartLinkAddress';
import { usePublishedPagesUsage } from '../../../../usePublishedPagesUsage';
import { SongStatusPill } from '../SongPageChips';
import { songPageMessages } from '../songPageMessages';
import { Panel, PanelBody, PanelHead } from '../../../../../music24/components/primitives/Panel';
import { usePreviewPageLang } from '../../../../smlPreview/usePreviewPageLang';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongSmartLinkCardProps {
  accountId: number;
  item: CatalogItem;
  readOnly: boolean;
  onEdit: (smartLinkId: number) => void;
  onCreate: () => void;
}

/**
 * The SmartLink of the song (design e1, e2): the pre-save page or the SmartLink with its address,
 * "Open the page" and "Edit"; or "Make a SmartLink" with the Free page count (f8 line 2). The
 * creation sheet checks the page limit itself (`M24_LIMIT_PAGES`).
 */
const SongSmartLinkCardComponent: React.FC<SongSmartLinkCardProps> = ({
  accountId,
  item,
  readOnly,
  onEdit,
  onCreate,
}) => {
  const intl = useIntl();
  const withLang = usePreviewPageLang();
  const address = useSmartLinkAddress(accountId, item.smartLinkSlug);
  const pages = usePublishedPagesUsage(accountId, { skip: item.smartLinkId !== null });
  const { smartLinkId, smartLinkStatus } = item;
  const hasLink = smartLinkId !== null && smartLinkStatus !== null;

  let pill = <SongStatusPill tone="off" label={intl.formatMessage(songPageMessages.smartLinkNone)} />;
  if (hasLink && item.presaveCampaignUuid !== null) {
    pill = <SongStatusPill tone="ok" label={intl.formatMessage(songPageMessages.chipPresave)} />;
  } else if (hasLink) {
    pill =
      smartLinkStatus === 'published' ? (
        <SongStatusPill tone="ok" label={intl.formatMessage(songPageMessages.smartLinkLive)} />
      ) : (
        <SongStatusPill tone="warn" label={intl.formatMessage(songPageMessages.smartLinkDraft)} />
      );
  }

  return (
    <Panel data-testid="song-page-smartlink">
      <PanelHead title={intl.formatMessage(songPageMessages.smartLinkTitle)} right={pill} />
      <PanelBody className={styles.cardBody}>
        {hasLink ? (
          <>
            <div className={styles.smartLink}>
              <div className={styles.grow}>
                <b>{item.title}</b>
                {address !== null && <div className={styles.mono}>{address}</div>}
              </div>
              {address !== null && smartLinkStatus === 'published' && (
                <SMLButton
                  size="sm"
                  outline
                  omitCap
                  leftIcon={faArrowUpRightFromSquare}
                  onClick={() => window.open(withLang(`https://${address}`), '_blank', 'noopener,noreferrer')}
                >
                  {intl.formatMessage(songPageMessages.smartLinkOpen)}
                </SMLButton>
              )}
              {!readOnly && (
                <SMLButton size="sm" outline omitCap onClick={() => onEdit(smartLinkId)}>
                  {intl.formatMessage(songPageMessages.smartLinkEdit)}
                </SMLButton>
              )}
            </div>
            {item.presaveCampaignUuid !== null && (
              <span className={styles.help}>{intl.formatMessage(songPageMessages.smartLinkPresaveHelp)}</span>
            )}
          </>
        ) : (
          <>
            <span className={styles.help}>
              {intl.formatMessage(songPageMessages.smartLinkPitch, { title: item.title })}
            </span>
            {pages.limit !== null && pages.used !== undefined && (
              <span className={styles.help}>
                {intl.formatMessage(songPageMessages.pagesLimit, { used: pages.used, limit: pages.limit })}
              </span>
            )}
            {!readOnly && (
              <div>
                <SMLButton color="primary" omitCap leftIcon={faPlus} onClick={onCreate}>
                  {intl.formatMessage(songPageMessages.smartLinkMake)}
                </SMLButton>
              </div>
            )}
          </>
        )}
      </PanelBody>
    </Panel>
  );
};

export const SongSmartLinkCard = TypedMemo(SongSmartLinkCardComponent);
